import { match } from "ts-pattern";
import {
  ScheduleV2Compat,
  compareDateTimeRecords,
  dateTimeRecordToLocalisedDateTime,
  dateTimeToDateTimeRecord,
  jsDateToDateTime,
  type DateTimeRecord,
  type ScheduleV2,
} from "@visioni/schedules";
import type { AlarmSchedule } from "../../alarm-schedules/types";
import type { AlarmScheduleV2 } from "../../schema/alarm-schedule";
import {
  currMode,
  currModeReason,
  currSyncState,
  nextMode,
  type NextMode,
  type Mode,
} from "../../schema/alarm-configuration";
import { mode as scheduleMode } from "../../schema/alarm-schedule";
import type { AlarmConfiguration, AlarmException, AlarmOverride, AlarmSilence } from "../../schema";

/**
 * The unified view model for the alarm control.
 *
 * Every UI-affecting distinction is a variant tag; every variant carries exactly
 * the data that variant needs. This is the "impossible states unrepresentable"
 * replacement for the prop-bag on the legacy `ArmDisarmControl`.
 *
 * Priority when multiple inputs apply: pending > activated > inOverride >
 * inException > applying > scheduled > unconfigured > loading.
 */
export type AlarmControlView =
  | { readonly kind: "loading" }
  // Used when the configuration cannot be obtained or trusted: the query
  // errored, or the response failed schema validation. Distinct from
  // `loading` so the UI doesn't sit on a spinner forever when something has
  // actually gone wrong. `reason` is a machine-readable discriminant so
  // downstream copy / telemetry can branch cleanly.
  | { readonly kind: "error"; readonly reason: "query" | "schema" }
  | { readonly kind: "unconfigured" }
  | ScheduledView
  | InExceptionView
  | InOverrideView
  | ActivatedView
  | ApplyingView
  // Transient client-side state between the user tapping an action and the
  // server accepting it. Keeps the same icon/copy as `underlying` so the card
  // doesn't visibly jump, but swaps the active button for a spinner so the
  // user has explicit confirmation that a command is in flight. On mutation
  // success this flips to `applying` (server-truth) once the refetched
  // configuration reports applied≠configured; on mutation error it flips
  // straight back to `underlying`.
  //
  // The `intent` kind decides *which* button spins — e.g. `intent=disarm` on
  // a `scheduled/armed` underlying shows the Disarm button with a spinner.
  | {
      readonly kind: "pending";
      readonly intent: PendingIntentKind;
      readonly underlying: ScheduledView | InExceptionView | InOverrideView | ActivatedView | ApplyingView;
    };

export type AlarmControlViewKind = AlarmControlView["kind"];

/** The subset of `AlarmControlCommand["kind"]` that the view layer needs to distinguish. */
export type PendingIntentKind = "arm" | "disarm" | "adjust" | "resume" | "silence" | "extendSilence";

export type ScheduledView = {
  readonly kind: "scheduled";
  readonly mode: Mode;
  readonly nextTransition: NextTransition | null;
  readonly timezone: string;
};

export type InExceptionView = {
  readonly kind: "inException";
  readonly mode: Mode;
  readonly exception: ActiveException;
  readonly nextTransition: NextTransition | null;
  readonly timezone: string;
};

export type InOverrideView = {
  readonly kind: "inOverride";
  readonly mode: Mode;
  readonly override: ActiveOverride;
  readonly underlying: "scheduled" | { readonly mode: Mode };
  // The schedule transition that would fire *if the override were cleared*.
  // Drives the Adjust row's "what the schedule would do next" copy so the
  // operator can weigh extending the override against letting the schedule
  // resume. `null` when the schedule has no further transitions in horizon.
  readonly nextTransition: NextTransition | null;
  // Pre-formatted "today at 6:00 PM" / "tomorrow at 6:00 PM" / "Wed at
  // 6:00 PM" label for the override's end time, OR `null` for an
  // indefinite override. Computed by `deriveView` against its `now`
  // input so the copy layer can stay pure — without this the copy
  // layer would have to call `getLocalisedNow(timezone)` to decide
  // "today/tomorrow/later", which makes `describe(view)` produce
  // different output depending on when it's called and breaks
  // story-render-vs-assertion consistency around midnight.
  readonly overrideEndLocalLabel: string | null;
  readonly timezone: string;
};

export type ActivatedView = {
  readonly kind: "activated";
  readonly mode: "armed";
  readonly silence: { readonly kind: "none" } | { readonly kind: "active"; readonly minutesRemaining: number };
  readonly timezone: string;
};

export type ApplyingView = {
  readonly kind: "applying";
  // The mode the camera is *currently* in (i.e. the applied schedule's
  // mode at `now`). Drives the lock icon — when the schedule is
  // disarming, the camera is still armed until the change lands, so
  // the lock should remain purple/closed throughout the transition.
  readonly currMode: Mode;
  // Which way the change is going. `direction === "disarming"` means
  // configured says disarmed but applied still says armed. Drives the
  // title copy ("Arming…" / "Disarming…") only.
  readonly direction: "arming" | "disarming";
  // The next transition the *configured* schedule (the one we're
  // applying) will produce after settling. Lets the user adjust what's
  // about to happen even while the previous change is still propagating.
  readonly nextTransition: NextTransition | null;
  readonly timezone: string;
};

/** A scheduled mode flip the UI advertises as "Arms ..." / "Disarms ..." copy. */
export type NextTransition = {
  readonly to: Mode;
  readonly at: DateTimeRecord;
  // Why the upcoming flip happens. Drives the Adjust row's *next subtitle*
  // so it tells the same story the *now subtitle* would once the
  // transition fires (e.g. an exception flip pre-announces the
  // "Exception: Weekend works" copy the camera will show after).
  readonly reason: NextTransitionReason;
};

/**
 * The cause of an upcoming mode flip. Mirrors `NextMode.reason` from the
 * schedule schema, with two additions:
 *
 *  - the exception's user-visible description is resolved here so the UI
 *    doesn't have to walk `configuration.settings.exceptions` again;
 *  - an `"applying"` wrapper case marks the transition as in-flight: the
 *    user has edited the schedule and the camera hasn't caught up. The
 *    preview comes from the *configured* (target) schedule — what the
 *    user wants — but the reason becomes generic so we don't claim to
 *    know the underlying cause until it lands.
 */
export type NextTransitionReason =
  | { readonly kind: "routine" }
  | {
      readonly kind: "exception";
      readonly action: "armed" | "disarmed";
      readonly description: string;
    }
  | { readonly kind: "override" }
  | { readonly kind: "default" }
  | { readonly kind: "applying" };

/** A planned exception that is currently in force. */
export type ActiveException = {
  readonly id: string;
  readonly reason: string;
  readonly action: "armed" | "disarmed";
  readonly period: AlarmException["period"];
};

/** A manual override that is currently in force. */
export type ActiveOverride = {
  readonly id: string;
  readonly reason: string;
  readonly action: "armed" | "disarmed";
  readonly period: AlarmOverride["period"];
};

/** External camera status feeding `deriveView`. */
export type CameraStatus = { readonly kind: "activated" } | { readonly kind: "idle" } | { readonly kind: "unknown" };

/**
 * Three-state signal the hook layer passes in. Keeps `deriveView` honest
 * about the difference between "haven't fetched yet" (loading), "couldn't
 * fetch / couldn't trust the response" (error), and "fetched a valid
 * configuration" (ready). The previous `configuration | null` shape
 * collapsed all three into the same sentinel, which silently produced a
 * forever-spinner on query/schema failure.
 */
export type ConfigurationSource =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly reason: "query" | "schema" }
  | { readonly kind: "ready"; readonly configuration: AlarmConfiguration };

export type DeriveViewInput = {
  readonly source: ConfigurationSource;
  readonly cameraStatus: CameraStatus;
  readonly silence: AlarmSilence | null;
  readonly now: Date;
  /**
   * When non-null, the user has dispatched a command that the server has not
   * yet acknowledged. `deriveView` wraps the would-be view in a `pending`
   * variant, which the component renders as the same card with a spinner on
   * the affected button. Null while no command is in flight.
   */
  readonly pendingIntent?: PendingIntentKind | null;
};

/**
 * The pure view-model derivation.
 *
 * Order of precedence:
 *   1. source=loading → loading
 *   2. source=error → error (reason forwarded)
 *   3. camera reports activated → activated (plus silence if active)
 *   4. configured but not yet applied → applying
 *   5. applied + an override evaluates to active → inOverride
 *   6. applied + an exception evaluates to active → inException
 *   7. applied routine state → scheduled
 *   8. no applied schedule → unconfigured
 *
 * If `pendingIntent` is non-null and the resulting view is one of the
 * action-bearing variants (`scheduled`, `inException`, `inOverride`,
 * `activated`, `applying`), the view is wrapped in `pending` so the UI
 * can spin the active button. Pending is ignored on `loading` / `error`
 * / `unconfigured` — there's no meaningful action to be "pending" in
 * those states, and showing a spinner there would be a lie.
 */
export const deriveView = (input: DeriveViewInput): AlarmControlView => {
  const baseView = deriveBaseView(input);
  return wrapPending(baseView, input.pendingIntent ?? null);
};

const wrapPending = (view: AlarmControlView, intent: PendingIntentKind | null): AlarmControlView => {
  if (intent === null) return view;
  return (
    match(view)
      .returnType<AlarmControlView>()
      .with({ kind: "scheduled" }, (v) => ({ kind: "pending", intent, underlying: v }))
      .with({ kind: "inException" }, (v) => ({ kind: "pending", intent, underlying: v }))
      .with({ kind: "inOverride" }, (v) => ({ kind: "pending", intent, underlying: v }))
      .with({ kind: "activated" }, (v) => ({ kind: "pending", intent, underlying: v }))
      // `applying` carries an Adjust row now (the user can re-target the
      // upcoming transition while the previous change is still
      // propagating), so an `adjust` command on `applying` is legitimate
      // and the spinner should sit on the Adjust button.
      .with({ kind: "applying" }, (v) => ({ kind: "pending", intent, underlying: v }))
      // A pending state is meaningless on loading/error/unconfigured —
      // there's no button the user could have pressed to start it. Fall
      // through and render the base variant. (In practice the hook never
      // dispatches from these states because `interpret` noops.)
      .otherwise(() => view)
  );
};

const deriveBaseView = (input: DeriveViewInput): AlarmControlView => {
  const { source, cameraStatus, silence, now } = input;

  if (source.kind === "loading") {
    return { kind: "loading" };
  }

  if (source.kind === "error") {
    return { kind: "error", reason: source.reason };
  }

  const { configuration } = source;
  const configurationV2 = toConfigurationV2(configuration);
  const syncState = currSyncState(configurationV2);

  // Source of user intent for copy (timezone, active override/exception).
  const activeSchedule = match(syncState)
    .with({ status: "unconfigured" }, () => null)
    .with({ status: "applying_initial" }, ({ configured }) => configured)
    .with({ status: "inherited" }, ({ applied }) => applied)
    .with({ status: "applying_change" }, ({ configured }) => configured)
    .with({ status: "applied" }, ({ applied }) => applied)
    .with({ status: "altered" }, ({ applied }) => applied)
    .exhaustive();

  const timezone = activeSchedule?.metadata.timezone ?? configuration.settings?.timezone ?? "UTC";

  // 2. Live activation trumps everything.
  if (cameraStatus.kind === "activated") {
    return {
      kind: "activated",
      mode: "armed",
      silence: computeSilenceView(silence, timezone, now),
      timezone,
    };
  }

  // 3. Applied schedule not yet present → we're in the middle of publishing the first.
  //    Treat as loading so the UI shows "Scheduled" shimmering rather than a spurious
  //    disarmed state. For applying_change we distinguish arming vs disarming.
  const applyingView = match(syncState)
    .returnType<AlarmControlView | null>()
    .with({ status: "unconfigured" }, () => ({ kind: "unconfigured" }))
    .with({ status: "applying_initial" }, () => ({ kind: "loading" }))
    .with({ status: "applying_change" }, ({ applied, configured }) => {
      // Compare each schedule on its own terms at `now`. If they agree on the
      // current mode, the switchover has effectively landed from the user's
      // POV — fall through to the normal scheduled/override/exception path.
      // `currMode` reflects the *applied* schedule (what the camera is
      // doing right now), so the lock icon doesn't flip until the change
      // actually lands.
      const atRecord = toNowRecord(now, timezone);
      const appliedMode = scheduleMode(applied, atRecord);
      const configuredMode = scheduleMode(configured, atRecord);
      const nextTransition = computeNextTransition(configuration, configurationV2, syncState, now, timezone);
      if (appliedMode === "armed" && configuredMode === "disarmed") {
        return { kind: "applying", currMode: appliedMode, direction: "disarming", nextTransition, timezone };
      }
      if (appliedMode === "disarmed" && configuredMode === "armed") {
        return { kind: "applying", currMode: appliedMode, direction: "arming", nextTransition, timezone };
      }
      return null;
    })
    .with({ status: "inherited" }, () => null)
    .with({ status: "applied" }, () => null)
    .with({ status: "altered" }, () => null)
    .exhaustive();

  if (applyingView !== null) {
    return applyingView;
  }

  // 4–7. Use the applied schedule for mode/override/exception detection.
  if (activeSchedule === null) {
    return { kind: "unconfigured" };
  }

  const mode = currMode(configurationV2, toNowRecord(now, timezone));
  if (mode === null) {
    return { kind: "unconfigured" };
  }

  // Ask the schedule evaluator which rule is currently winning. This keeps
  // the UI's "current state reason" aligned with whatever precedence rules
  // `@visioni/schedules` uses internally (override > exceptions > routine
  // today, but the UI doesn't need to know — if the precedence ever
  // changes, this branches the right way automatically). Specifically,
  // when several exceptions overlap, the schedule's own evaluator picks
  // the winner; we used to scan `settings.exceptions` first-match, which
  // could disagree with the schedule.
  const settings = configuration.settings;
  const reason = currModeReason(configurationV2, toNowRecord(now, timezone));
  const nextTransition = computeNextTransition(configuration, configurationV2, syncState, now, timezone);

  if (reason?.kind === "override") {
    const activeOverride = resolveOverrideFromSettings(settings?.override ?? null);
    if (activeOverride !== null) {
      return {
        kind: "inOverride",
        mode,
        override: activeOverride,
        // For now we advertise underlying as "scheduled" — refining this (so the UI
        // can say "Manually armed on top of scheduled armed") is deferred. The
        // variant shape supports it already.
        underlying: "scheduled",
        nextTransition,
        overrideEndLocalLabel:
          activeOverride.period.end === null ?
            null
          : formatLocalDateTimeLabel(activeOverride.period.end, timezone, now),
        timezone,
      };
    }
    // Fall through if the override metadata in `settings` has been edited
    // out from under the applied schedule — render scheduled instead.
  }

  if (reason?.kind === "exception") {
    const activeException = resolveExceptionFromSettings(settings?.exceptions ?? [], reason.meta.id);
    if (activeException !== null) {
      return {
        kind: "inException",
        mode,
        exception: activeException,
        nextTransition,
        timezone,
      };
    }
    // Same fallback rationale as override.
  }

  return {
    kind: "scheduled",
    mode,
    nextTransition,
    timezone,
  };
};

// #region Helpers

/**
 * Bridge the app-facing {@link AlarmConfiguration} (V1 schedules plus `settings`)
 * to the V2-shaped config consumed by `currSyncState` / `currMode` / `nextMode`.
 *
 * `ScheduleV2Compat.fromV1` produces a generic `ScheduleV2` whose `rules` are
 * keyed by rule id rather than the named slots (`routine`, `exceptions`,
 * `silence`, `override`) used by the typed `AlarmScheduleV2`. That's fine at
 * runtime because the helpers only read `metadata` directly and delegate
 * rule-shape work to `ScheduleV2Compat.toV1`, which accepts either shape. We
 * expose the bridged value as `AlarmScheduleV2` via a targeted cast in
 * {@link asAlarmScheduleV2}; the cast is confined here so the public API can
 * consume the existing helpers unchanged.
 */
const toConfigurationV2 = (
  configuration: AlarmConfiguration,
): {
  applied: AlarmScheduleV2 | null;
  configured: AlarmScheduleV2 | null;
} => {
  const configuredSchedule: AlarmSchedule | null = configuration.configured?.schedule ?? null;
  const appliedSchedule: AlarmSchedule | null = match(configuration.applied)
    .with("configured", () => configuredSchedule)
    .with(null, () => null)
    .otherwise((a) => a.schedule);

  return {
    configured: configuredSchedule === null ? null : upgradeToV2(configuredSchedule),
    applied: appliedSchedule === null ? null : upgradeToV2(appliedSchedule),
  };
};

const upgradeToV2 = (schedule: AlarmSchedule): AlarmScheduleV2 => asAlarmScheduleV2(ScheduleV2Compat.fromV1(schedule));

/**
 * Narrow a generic `ScheduleV2` (flat rule id keys) to the typed
 * `AlarmScheduleV2` shape consumed by `schema/alarm-configuration.ts`. The
 * helpers there only use the parts of the shape that are identical between
 * the two (metadata + rule map), so this is sound at runtime — we isolate the
 * unsafe-looking cast here rather than letting it leak through call sites.
 */
const asAlarmScheduleV2 = (schedule: ScheduleV2): AlarmScheduleV2 => {
  // Round-trip through `unknown` so TS accepts the narrowing without a direct
  // structural match. This is intentional: see comment above.
  const erased: unknown = schedule;
  return erased as AlarmScheduleV2;
};

const toNowRecord = (now: Date, timezone: string): DateTimeRecord =>
  dateTimeToDateTimeRecord(jsDateToDateTime(now).setZone(timezone));

// `nextMode` searches up to MAX_HORIZON_DAYS internally; we pass a generous cap.
const horizonFromNow = (now: Date, timezone: string): DateTimeRecord =>
  dateTimeToDateTimeRecord(jsDateToDateTime(now).setZone(timezone).plus({ days: 30 }));

/**
 * Compute the next transition the UI should advertise.
 *
 * Always uses the **applied** schedule for the preview — that's what the
 * camera will actually do next, because the configured schedule (the
 * user's intent) hasn't landed yet.
 *
 * The `reason` subtitle, however, depends on whether the user has
 * pending edits that affect the upcoming flip:
 *
 *  - **Steady state** (`applied === configured`, or `applying_change` but
 *    the imminent transition is unaffected by the edit): use the
 *    applied schedule's reason — `routine` / `exception` / `override` /
 *    `default`.
 *
 *  - **Applying change with imminent delta** (`applying_change` AND the
 *    configured schedule would produce a different next transition):
 *    tag the reason `applying`. The next-row subtitle reads "Applying
 *    changes", matching the now-row subtitle of the `applying` view —
 *    one consistent signal everywhere a pending edit is reshaping the
 *    near horizon.
 *
 * The "different next transition" check compares mode (`to`), instant
 * (`at`), and reason kind. A change that produces the same flip at the
 * same time and for the same reason isn't worth flagging.
 */
const computeNextTransition = (
  configuration: AlarmConfiguration,
  configurationV2: { applied: AlarmScheduleV2 | null; configured: AlarmScheduleV2 | null },
  syncState: ReturnType<typeof currSyncState>,
  now: Date,
  timezone: string,
): NextTransition | null => {
  const horizonAt = horizonFromNow(now, timezone);
  const atRecord = toNowRecord(now, timezone);

  // Truth: what the still-applied schedule will do next.
  const appliedNext = nextMode(configurationV2, atRecord, horizonAt);
  if (appliedNext === null) return null;

  const reason: NextTransitionReason =
    syncState.status === "applying_change" ?
      (() => {
        // Run `nextMode` against a synthetic configuration whose
        // `applied` slot points at the configured schedule. That tells
        // us what the *new* (target) schedule would produce at the same
        // horizon. If the two next-transitions agree, the user's edit
        // doesn't reshape the near horizon and the regular reason is
        // honest. Otherwise the upcoming flip is in flux — surface it.
        const configuredAsApplied = { ...configurationV2, applied: configurationV2.configured };
        const configuredNext = nextMode(configuredAsApplied, atRecord, horizonAt);
        return nextModesAgreeOnSurface(appliedNext, configuredNext) ?
            toNextTransitionReason(appliedNext.reason, configuration)
          : { kind: "applying" };
      })()
    : toNextTransitionReason(appliedNext.reason, configuration);

  return { to: appliedNext.mode, at: appliedNext.at, reason };
};

/**
 * Two `NextMode`s agree on the user-visible surface (mode + instant +
 * reason kind) so the upcoming flip would read identically before and
 * after the configured schedule lands. The reason's `meta` (e.g. which
 * specific exception id) is intentionally ignored — the user-facing
 * copy doesn't differentiate between two exceptions with the same
 * `kind: "exception"` reason.
 */
const nextModesAgreeOnSurface = (a: NextMode, b: NextMode | null): boolean => {
  if (b === null) return false;
  if (a.mode !== b.mode) return false;
  if (compareDateTimeRecords(a.at, b.at) !== 0) return false;
  return a.reason.kind === b.reason.kind;
};

/**
 * Translate the schema-layer `NextMode.reason` (which carries a
 * `meta: {id}` for exception/override-driven transitions) into a
 * UI-friendly {@link NextTransitionReason} with the exception's
 * user-visible description resolved. The lookup happens here — the UI
 * layer never has to walk back through `configuration.settings.exceptions`
 * to find the matching reason text.
 */
const toNextTransitionReason = (reason: NextMode["reason"], configuration: AlarmConfiguration): NextTransitionReason =>
  match(reason)
    .returnType<NextTransitionReason>()
    .with({ kind: "routine" }, () => ({ kind: "routine" }))
    .with({ kind: "default" }, () => ({ kind: "default" }))
    .with({ kind: "override" }, () => ({ kind: "override" }))
    .with({ kind: "exception" }, ({ meta }) => {
      const exception = (configuration.settings?.exceptions ?? []).find((ex) => ex.id === meta.id);
      // Fall back to a generic description if the exception was deleted
      // between the schedule snapshot and now — better than rendering
      // empty copy.
      return {
        kind: "exception",
        action: exception?.action ?? "armed",
        description: exception?.reason ?? "Planned change",
      };
    })
    .exhaustive();

const isWithin = (nowRecord: DateTimeRecord, start: DateTimeRecord, end: DateTimeRecord | null): boolean => {
  if (compareDateTimeRecords(nowRecord, start) < 0) return false;
  if (end === null) return true;
  return compareDateTimeRecords(nowRecord, end) < 0;
};

/**
 * The schedule evaluator already told us "an override is winning right now"
 * (via `currModeReason`); this helper just looks the override metadata up in
 * `settings` so the view can render its period / reason copy. Returns
 * `null` if the override was deleted from `settings` between the schedule
 * snapshot and now (which only happens during an `applying_change` where
 * the user is removing the override they're currently in).
 */
const resolveOverrideFromSettings = (override: AlarmOverride | null): ActiveOverride | null =>
  override === null ? null : (
    {
      id: override.id,
      reason: override.reason,
      action: override.action,
      period: override.period,
    }
  );

/**
 * Sibling to {@link resolveOverrideFromSettings} — looks up an exception by
 * the rule id the schedule evaluator picked. We don't re-scan the
 * exceptions list ourselves any more; if the schedule says exception
 * X is the active rule, we trust it and just resolve the display data.
 */
const resolveExceptionFromSettings = (exceptions: readonly AlarmException[], id: string): ActiveException | null => {
  const ex = exceptions.find((e) => e.id === id);
  if (ex === undefined) return null;
  return {
    id: ex.id,
    reason: ex.reason,
    action: ex.action,
    period: ex.period,
  };
};

const computeSilenceView = (
  silence: AlarmSilence | null,
  timezone: string,
  now: Date,
): { readonly kind: "none" } | { readonly kind: "active"; readonly minutesRemaining: number } => {
  if (silence === null) return { kind: "none" };
  const nowRecord = toNowRecord(now, timezone);
  if (!isWithin(nowRecord, silence.period.start, silence.period.end)) {
    return { kind: "none" };
  }
  const nowDt = jsDateToDateTime(now).setZone(timezone);
  const endDt = dateTimeRecordToLocalisedDateTime(silence.period.end, timezone);
  const diffMs = endDt.toMillis() - nowDt.toMillis();
  const minutesRemaining = Math.max(0, Math.ceil(diffMs / 60_000));
  return { kind: "active", minutesRemaining };
};

/**
 * Format a `DateTimeRecord` as `today at h:mm a`, `tomorrow at h:mm a`,
 * or `Wed at h:mm a` relative to {@link now}, expressed in {@link timezone}.
 *
 * Lives in the model layer (not `copy.ts`) so the time-relative
 * classification consumes `deriveView`'s explicit `now` input rather than
 * `getLocalisedNow()` at copy-render time. That keeps `describe(view)`
 * pure: same view → same output regardless of when it's read, which is
 * what the Storybook story-render-vs-assertion contract relies on.
 */
const formatLocalDateTimeLabel = (at: DateTimeRecord, timezone: string, now: Date): string => {
  const dt = dateTimeRecordToLocalisedDateTime(at, timezone);
  const nowDt = jsDateToDateTime(now).setZone(timezone);
  const time = dt.toFormat("h:mm a");
  if (dt.hasSame(nowDt, "day")) return `today at ${time}`;
  if (dt.hasSame(nowDt.plus({ days: 1 }), "day")) return `tomorrow at ${time}`;
  return `${dt.toFormat("ccc")} at ${time}`;
};

// #endregion
